import React, { useCallback } from 'react'
import { styled } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import { Typography } from '@mui/material'

import { formatUnits, parseUnits } from 'ethers/lib/utils'
import { useWeb3React } from '@web3-react/core'
import {
  Web3Provider,
} from '@ethersproject/providers'

import { useAppDispatch, useAppSelector } from '../../app/hooks'
import { ModalType, openModal } from '../../features/modal/modalSlice'
import {
  AddTokenButton, TokenButton,
} from '../buttons'
import {
  getSelectedDexToken, setDexIndexer, setDexSelectedAmount, TokenIndexer, TokenState,
} from '../../features/dex/dexSlice'
import { AccountBalance } from '../web3'

const InputBox = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1),
  backgroundColor: (theme.palette.mode === 'light') ? '#f5f5f9' : '#393939',
  padding: theme.spacing(2),
  borderRadius: 16,
}))

interface DexInputProps {
  indexer: TokenIndexer
}

const DexInput = ({
  indexer,
}: DexInputProps): JSX.Element => {
  const dispatch = useAppDispatch()
  const {
    active, chainId, account, library: provider,
  } = useWeb3React<Web3Provider>()

  const token = useAppSelector(getSelectedDexToken(indexer))

  const { currency, selectedAmount } = token as TokenState

  const handleTokenButton = () => {
    dispatch(setDexIndexer(indexer))
    dispatch(openModal(ModalType.SELECT_DEX_TOKEN))
  }

  const parsedAmount = selectedAmount
    ? +formatUnits(selectedAmount, currency?.decimals)
    : ''

  const onInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (!currency) return

    let { value } = event.target
    value = +value >= 0 ? value : ''

    if (value.includes('-') || value.length > 32) return

    const splitedV = value.split('.')[1]
    if (splitedV && splitedV.length > currency.decimals) return

    if (!currency) return

    dispatch(setDexSelectedAmount({
      indexer,
      amount: value ? parseUnits(value, currency.decimals) : undefined,
      update: true,
    }))
  }, [dispatch, currency, indexer])

  return (
    <InputBox>
      <Grid container>
        <Grid
          item
          xs={12}
          display="flex"
          sx={{ justifyContent: 'space-between', marginBottom: 2 }}
        >
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
          }}
          />
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
          }}
          >
            {active && chainId && account && provider && currency ? (
              <AccountBalance
                account={account}
                indexer={indexer}
                currency={currency}
                showMax
                showSymbol
              />
            ) : (
              <Typography
                variant="body2"
                color="secondary"
                noWrap
                sx={{ marginRight: 0.5 }}
              >
                0
              </Typography>
            )}
          </Box>
        </Grid>
        <Grid item xs={8} display="flex">
          <TextField
            fullWidth
            placeholder="0.0"
            InputProps={{
              // patern: '[0-9]*(.[0-9]+)?',
              sx: {
                borderRadius: 0,
                fontSize: 24,
              },
              disableUnderline: (!currency),
            }}
            onChange={onInputChange}
            variant="standard"
            autoComplete="off"
            autoCorrect="off"
            InputLabelProps={{ shrink: false, color: 'secondary' }}
            color="secondary"
            type="number"
            value={parsedAmount}
            disabled={!currency}
          />
        </Grid>
        <Grid item xs={4} display="flex">
          <TokenButton
            symbol={currency?.symbol}
            logoURI={currency?.logoURI}
            onClick={handleTokenButton}
            asEmpty
          />
        </Grid>
        <Grid item xs={8} display="flex" />
        <Grid item xs={4} display="flex">
          {
            currency
              && !currency.isNative
              && provider
              ? (
                <AddTokenButton
                  provider={provider}
                  currency={currency}
                  fullWidth
                />
              ) : (
                <Box sx={{ height: 30 }} />
              )
          }
        </Grid>
      </Grid>
    </InputBox>
  )
}

export default DexInput
